import agentAPI from '@/api/agentAPI'
import { bus as realtimeBus } from '@/plugins/realtimeBus'

/**
 * Build a context object describing where the user currently is.
 * The agent system prompt gets this injected so it can answer questions
 * like "summarize this assignment" without the user having to specify.
 *
 * @param {object} rootState — full Vuex root state
 * @returns {object|null}
 */
function buildRouteContext(rootState) {
    try {
        // Pull via window globals since we don't have the Vue Router $route in the store directly
        const url = window.location && window.location.pathname
        const path = url || ''
        const ctx = { url: path }

        // Derive group/course/material/element from the URL path parts.
        // Common Edaptio patterns:
        //   /group/:group_id/...
        //   /group/:group_id/courses/:course_id/lesson/:material_id/:material_element_id?
        //   /group/:group_id/assignment/:assignment_id
        const g = path.match(/\/groups?\/(\d+)/); if (g) ctx.group_id = parseInt(g[1], 10)
        const c = path.match(/\/courses?\/(\d+)/); if (c) ctx.course_id = parseInt(c[1], 10)
        const l = path.match(/\/lesson\/(\d+)/); if (l) ctx.material_id = parseInt(l[1], 10)
        const e = path.match(/\/lesson\/\d+\/(\d+)/); if (e) ctx.material_element_id = parseInt(e[1], 10)
        const a = path.match(/\/assignment\/(\d+)/); if (a) ctx.assignment_id = parseInt(a[1], 10)

        // Resolve human-readable names from Vuex so the agent can tell what language
        // the class is taught in (e.g. "English 9A" → English), without a round-trip.
        if (ctx.group_id && rootState && rootState.groupStore && rootState.groupStore.info) {
            const info = rootState.groupStore.info[ctx.group_id]
            if (info) {
                if (info.group_name) ctx.group_name = String(info.group_name)
                if (info.description) ctx.group_description = String(info.description).slice(0, 500)
            }
        }
        if (ctx.group_id && ctx.course_id && rootState && rootState.courseStore && rootState.courseStore.courses) {
            const list = rootState.courseStore.courses[ctx.group_id] || []
            const match = list.find((x) => parseInt(x.course_id || x.id, 10) === ctx.course_id)
            if (match && match.title) ctx.course_name = String(match.title)
        }
        if (ctx.group_id && ctx.material_id && rootState && rootState.materialStore && rootState.materialStore.materials) {
            const list = rootState.materialStore.materials[ctx.group_id] || []
            const match = list.find((x) => parseInt(x.group_material_id || x.material_id || x.id, 10) === ctx.material_id)
            if (match && match.title) ctx.lesson_name = String(match.title)
        }

        // Institution id from userStore if available
        if (rootState && rootState.userStore && rootState.userStore.default_institution) {
            ctx.institution_id = parseInt(rootState.userStore.default_institution.group_id, 10)
        }

        // User's preferred language — resolved like App.vue's setLanguage():
        //   1. explicit user pref
        //   2. institution default
        //   3. fallback 'en'
        // Normalize to short code (da_DK → da)
        let lang = ''
        if (rootState && rootState.userStore) {
            const us = rootState.userStore
            const userLang = us.user_settings && us.user_settings.language
            const instId = us.default_institution && us.default_institution.group_id
            const instLang = instId && us.institutions_settings && us.institutions_settings[instId]
                && us.institutions_settings[instId]['institution-settings-language']
            lang = userLang || instLang || 'en'
        } else {
            lang = 'en'
        }
        ctx.user_language = String(lang).split(/[-_]/)[0].toLowerCase()

        // Page title for coarse-grain context
        if (typeof document !== 'undefined' && document.title) {
            ctx.page_title = document.title
        }
        return ctx
    } catch {
        return null
    }
}

/**
 * Vuex module for the AI agent.
 *
 * Phase 4 chunk 5 swapped the transport from WebSocket-to-Cloudflare-Worker
 * to SSE-against-the-Edaptio-API. The store no longer manages a long-lived
 * connection or a session token — every send is a one-shot fetch returning
 * a Server-Sent Events stream of Vercel AI SDK protocol frames.
 *
 * Persisted state (localStorage): {bubbleOpen, conversationId}.
 * conversationId is now a UUID v7 string, not a number — the migration was
 * 2026-05-05 with the laravel/ai cutover.
 */
const STORAGE_KEY = 'edaptio_agent_state_v1'

function loadPersisted() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return {}
        const parsed = JSON.parse(raw)
        return {
            bubbleOpen: !!parsed.bubbleOpen,
            // conversationId is a UUID string; clear stale numeric ids saved
            // from the Worker era so they don't 404 against the new endpoints.
            conversationId: typeof parsed.conversationId === 'string' ? parsed.conversationId : null,
        }
    } catch {
        return {}
    }
}
function persist(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            bubbleOpen: state.bubbleOpen,
            conversationId: state.conversationId,
        }))
    } catch {
        /* ignore quota errors */
    }
}

/**
 * Tools that only READ data. Completing one of these must NOT trigger a
 * content refresh in the UI. Everything else is treated as a write so that any
 * new mutating tool is covered by default — a stray refresh is harmless, a
 * missed one (stale UI after Olly edits) is the bug we're fixing.
 */
function isReadOnlyTool(name) {
    return /^(list_|lookup_|find_|get_)/.test(name || '') || name === 'class_overview'
}

/**
 * Best-effort extraction of the group/course/lesson a tool touched, so content
 * views can decide whether an agent edit concerns what they're currently
 * showing. Prefers the tool's own args/result; falls back to the page the user
 * is on. The success envelope is {ok, data:{...}}, so ids may sit at either level.
 */
function affectedIds(args, result, routeContext) {
    const a = (args && typeof args === 'object') ? args : {}
    const r = (result && typeof result === 'object')
        ? { ...(result.data && typeof result.data === 'object' ? result.data : {}), ...result }
        : {}
    const ctx = routeContext || {}
    const toInt = (v) => {
        const n = parseInt(v, 10)
        return Number.isNaN(n) ? null : n
    }
    return {
        groupId: toInt(a.group_id ?? r.group_id ?? ctx.group_id),
        courseId: toInt(a.course_id ?? r.course_id ?? ctx.course_id),
        materialId: toInt(a.material_id ?? a.group_material_id ?? r.material_id ?? r.group_material_id ?? ctx.material_id),
    }
}

/**
 * After a WRITE tool resolves, announce it on the realtime bus so any open
 * content view (e.g. the lesson/elements view) can re-fetch and show the change
 * live — covering create / update / delete plus move / reorder, since the
 * listener just force-refreshes. Generic on purpose: other views can subscribe
 * to `agent-content-changed` too.
 */
function announceContentChange(state, toolCallId, result, routeContext) {
    try {
        const msg = state.messages.find((m) => m.id === state.streamMessageId)
        const tc = msg && msg.toolCalls.find((t) => t.toolCallId === toolCallId)
        if (!tc || isReadOnlyTool(tc.name)) return
        // Skip failed writes — the envelope is {ok:false,...} on failure.
        if (result && typeof result === 'object' && result.ok === false) return
        realtimeBus.$emit('agent-content-changed', { tool: tc.name, ...affectedIds(tc.args, result, routeContext) })
    } catch { /* never let a refresh hint break the stream */ }
}

export const agentStore = {
    namespaced: true,
    state: () => ({
        ...{
            conversationId: null,
            streaming: false,
            streamBuffer: '',
            streamMessageId: null,    // server-assigned messageId for the in-flight assistant turn
            messages: [],             // [{id, role: 'user'|'assistant', content, toolCalls: []}]
            messagesLoading: false,
            conversations: [],
            conversationsLoaded: false,
            abortController: null,    // AbortController for the in-flight chat fetch
            bubbleOpen: false,
            unreadCount: 0,
            error: null,
        },
        ...loadPersisted(),
    }),
    mutations: {
        SET_CONVERSATION_ID(state, id) {
            state.conversationId = id
            persist(state)
        },
        SET_ABORT_CONTROLLER(state, ctrl) { state.abortController = ctrl },
        SET_STREAMING(state, val) { state.streaming = val },
        APPEND_USER_MESSAGE(state, content) {
            state.messages.push({ id: 'u-' + Date.now(), role: 'user', content, toolCalls: [] })
        },
        SET_STREAMING_START(state, messageId) {
            // Don't push an empty assistant message here. Two reasons:
            //   1. With Gemini, several seconds can pass between "stream
            //      opens" and "first delta or tool call" (model is
            //      reasoning silently). An empty <div> bubble during that
            //      window is visual noise + makes the typing-dots banner
            //      below look like a separate ghost message.
            //   2. The assistant message lazy-creates on the first
            //      APPEND_DELTA / APPEND_TOOL_CALL — same shape, no pre-
            //      allocation needed.
            state.streaming = true
            state.streamBuffer = ''
            state.streamMessageId = messageId || ('a-' + Date.now())
        },
        APPEND_DELTA(state, text) {
            state.streamBuffer += text
            let msg = state.messages.find((m) => m.id === state.streamMessageId)
            if (!msg) {
                msg = { id: state.streamMessageId, role: 'assistant', content: '', toolCalls: [] }
                state.messages.push(msg)
            }
            msg.content = state.streamBuffer
        },
        APPEND_TOOL_CALL(state, { toolCallId, name, args }) {
            let msg = state.messages.find((m) => m.id === state.streamMessageId)
            if (!msg) {
                msg = { id: state.streamMessageId, role: 'assistant', content: '', toolCalls: [] }
                state.messages.push(msg)
            }
            msg.toolCalls.push({ toolCallId, name, args, status: 'running' })
        },
        RESOLVE_TOOL_CALL(state, { toolCallId, result }) {
            const msg = state.messages.find((m) => m.id === state.streamMessageId)
            if (!msg) return
            // Vercel AI SDK protocol: `tool-output-available` carries
            // `toolCallId` but NOT `toolName`, so name-based matching
            // leaves chips stuck in "running" forever (see image #6 in
            // the QA cycle). Match by toolCallId — every tool-input-
            // available frame seeded one onto the chip.
            const tc = msg.toolCalls.find((t) => t.toolCallId === toolCallId)
            if (tc) {
                tc.status = 'done'
                tc.result = result
            }
        },
        FINISH_ASSISTANT_MESSAGE(state) {
            state.streaming = false
            state.streamBuffer = ''
            state.streamMessageId = null
            if (!state.bubbleOpen) {
                state.unreadCount++
            }
        },
        TOGGLE_BUBBLE(state) {
            state.bubbleOpen = !state.bubbleOpen
            if (state.bubbleOpen) state.unreadCount = 0
            persist(state)
        },
        SET_BUBBLE_OPEN(state, val) {
            state.bubbleOpen = val
            if (val) state.unreadCount = 0
            persist(state)
        },
        SET_CONVERSATIONS(state, list) {
            state.conversations = Array.isArray(list) ? list : []
            state.conversationsLoaded = true
        },
        UPSERT_CONVERSATION_META(state, meta) {
            if (!meta || !meta.conversation_id) return
            const idx = state.conversations.findIndex((c) => c.conversation_id === meta.conversation_id)
            if (idx >= 0) {
                state.conversations.splice(idx, 1, { ...state.conversations[idx], ...meta })
            } else {
                state.conversations.unshift(meta)
            }
        },
        REMOVE_CONVERSATION(state, cid) {
            state.conversations = state.conversations.filter((c) => c.conversation_id !== cid)
        },
        REPLACE_MESSAGES(state, messages) {
            state.messages = Array.isArray(messages) ? messages : []
            state.streamBuffer = ''
            state.streaming = false
            state.streamMessageId = null
        },
        SET_MESSAGES_LOADING(state, val) {
            state.messagesLoading = !!val
        },
        SET_ERROR(state, err) { state.error = err },
        RESET(state) {
            state.conversationId = null
            state.streaming = false
            state.streamBuffer = ''
            state.streamMessageId = null
            state.messages = []
            state.unreadCount = 0
            state.error = null
            persist(state)
        },
    },
    actions: {
        /**
         * Send a message: POST /agent/chat, then read the SSE stream and
         * dispatch protocol frames into the existing bubble UI mutations.
         */
        async sendMessage({ state, commit, rootState }, content) {
            if (!content || !content.trim()) return
            // Cancel any in-flight stream — only one assistant turn at a time.
            if (state.abortController) {
                try { state.abortController.abort() } catch { /* ignore */ }
            }
            const ac = new AbortController()
            commit('SET_ABORT_CONTROLLER', ac)
            commit('SET_ERROR', null)

            commit('APPEND_USER_MESSAGE', content)

            // Show the thinking dots immediately, not after the fetch resolves.
            // fetch() for an SSE response doesn't settle until the server
            // flushes the first body chunk, and with Gemini the model can
            // reason silently for several seconds — often emitting a tool call
            // as its first frame. Flipping `streaming` here (rather than only at
            // SET_STREAMING_START below, post-fetch) keeps the indicator up
            // across the network round-trip + that silent window. The early
            // returns below clear it again on a failed start.
            commit('SET_STREAMING', true)

            // Page context goes into a separate field, not prepended to the
            // message. The API merges it into the agent's system prompt so
            // it stays out of the persisted user message (otherwise the
            // raw JSON blob shows up in the chat history after refresh).
            const context = buildRouteContext(rootState)

            let response
            try {
                response = await agentAPI.chatStream({
                    message: content,
                    conversationId: state.conversationId,
                    context,
                    signal: ac.signal,
                })
            } catch (err) {
                if (err.name !== 'AbortError') {
                    commit('SET_ERROR', err.message || 'failed to start chat')
                }
                commit('SET_STREAMING', false)
                commit('SET_ABORT_CONTROLLER', null)
                return
            }
            if (!response.ok) {
                commit('SET_ERROR', `chat failed (${response.status})`)
                commit('SET_STREAMING', false)
                commit('SET_ABORT_CONTROLLER', null)
                return
            }
            // Persist the conversation_id from the response header — the
            // first turn of a fresh session needs this so subsequent turns
            // continue the same conversation row.
            const newConvId = response.headers.get('X-Conversation-Id')
            if (newConvId && newConvId !== state.conversationId) {
                commit('SET_CONVERSATION_ID', newConvId)
            }

            commit('SET_STREAMING_START', null)

            try {
                await consumeSseStream(response, {
                    onStart: (frame) => {
                        // Adopt the server-assigned messageId so the
                        // assistant turn keys to the same id the API will
                        // persist. Safe to re-call SET_STREAMING_START —
                        // it doesn't push a message anymore (the lazy-push
                        // happens on first delta / tool call instead).
                        if (frame.messageId && state.streamMessageId !== frame.messageId) {
                            commit('SET_STREAMING_START', frame.messageId)
                        }
                    },
                    onTextDelta: (text) => commit('APPEND_DELTA', text),
                    onToolStart: (toolCallId, name, args) => commit('APPEND_TOOL_CALL', { toolCallId, name, args }),
                    onToolResult: (toolCallId, result) => {
                        commit('RESOLVE_TOOL_CALL', { toolCallId, result })
                        // Tell open content views to refresh if this was a write.
                        announceContentChange(state, toolCallId, result, context)
                    },
                    onError: (msg) => commit('SET_ERROR', msg),
                })
            } catch (err) {
                if (err.name !== 'AbortError') {
                    commit('SET_ERROR', err.message || 'stream interrupted')
                }
            } finally {
                commit('FINISH_ASSISTANT_MESSAGE')
                commit('SET_ABORT_CONTROLLER', null)
            }
        },

        cancelStream({ state, commit }) {
            if (state.abortController) {
                try { state.abortController.abort() } catch { /* ignore */ }
            }
            commit('SET_ABORT_CONTROLLER', null)
            commit('FINISH_ASSISTANT_MESSAGE')
        },

        async fetchConversations({ commit }) {
            try {
                const res = await agentAPI.listConversations()
                const list = (res && res.data && res.data.data && res.data.data.conversations) || []
                commit('SET_CONVERSATIONS', list)
            } catch (err) {
                commit('SET_ERROR', err.message || 'failed to list conversations')
            }
        },

        async loadConversation({ commit }, conversationId) {
            if (!conversationId) return
            commit('SET_MESSAGES_LOADING', true)
            try {
                const res = await agentAPI.loadConversation(conversationId)
                const data = (res && res.data && res.data.data) || {}
                const mapped = (data.messages || [])
                    .filter((m) => m.role !== 'tool')
                    .map((m) => ({
                        id: 'm-' + m.message_id,
                        role: m.role,
                        content: m.content || '',
                        toolCalls: Array.isArray(m.tool_calls)
                            ? m.tool_calls.map((tc) => ({ name: tc.name, args: tc.args, status: 'done', result: tc.result }))
                            : [],
                    }))
                commit('REPLACE_MESSAGES', mapped)
                commit('SET_CONVERSATION_ID', conversationId)
            } catch (err) {
                commit('SET_ERROR', err.message || 'failed to load conversation')
            } finally {
                commit('SET_MESSAGES_LOADING', false)
            }
        },

        newConversation({ commit }) {
            commit('REPLACE_MESSAGES', [])
            commit('SET_CONVERSATION_ID', null)
        },

        async archiveConversation({ state, commit, dispatch }, conversationId) {
            try {
                await agentAPI.archiveConversation(conversationId)
                commit('REMOVE_CONVERSATION', conversationId)
                if (state.conversationId === conversationId) {
                    dispatch('newConversation')
                }
            } catch (err) {
                commit('SET_ERROR', err.message || 'failed to archive conversation')
            }
        },

        toggleBubble({ commit }) { commit('TOGGLE_BUBBLE') },
        expandBubble({ commit }) { commit('SET_BUBBLE_OPEN', true) },
        minimizeBubble({ commit }) { commit('SET_BUBBLE_OPEN', false) },
    },
}

/**
 * Consume a Vercel AI SDK protocol SSE stream from a Fetch Response. Each
 * `data: <json>` line is parsed and dispatched to the appropriate hook.
 *
 * Frame types we handle:
 *   start                   — opens the assistant turn (carries messageId)
 *   text-delta              — append text to the visible bubble
 *   tool-input-available    — tool call started; show "running…" chip
 *   tool-output-available   — tool call resolved; show result
 *   finish                  — turn complete (the closing [DONE] does the rest)
 *   error                   — surface to the bubble's error state
 *
 * Frames we ignore (reserved / not surfaced in the bubble UI):
 *   text-start, text-end, tool-input-start, tool-input-delta
 */
async function consumeSseStream(response, hooks) {
    if (!response.body) {
        throw new Error('no body on chat response')
    }
    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let buffer = ''

    while (true) { // eslint-disable-line no-constant-condition
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // SSE event delimiter is a blank line. Process complete events.
        let split
        while ((split = buffer.indexOf('\n\n')) !== -1) {
            const raw = buffer.slice(0, split)
            buffer = buffer.slice(split + 2)
            const dataLines = raw.split('\n').filter((l) => l.startsWith('data:'))
            if (dataLines.length === 0) continue
            const payload = dataLines.map((l) => l.slice(5).trim()).join('\n')
            if (payload === '[DONE]') return
            let frame
            try { frame = JSON.parse(payload) } catch { continue }
            const t = frame.type
            if (t === 'start') {
                hooks.onStart && hooks.onStart(frame)
            } else if (t === 'text-delta') {
                if (typeof frame.delta === 'string') hooks.onTextDelta && hooks.onTextDelta(frame.delta)
            } else if (t === 'tool-input-available') {
                hooks.onToolStart && hooks.onToolStart(frame.toolCallId, frame.toolName, frame.input)
            } else if (t === 'tool-output-available') {
                let parsedOutput = frame.output
                if (typeof parsedOutput === 'string') {
                    try { parsedOutput = JSON.parse(parsedOutput) } catch { /* keep string */ }
                }
                hooks.onToolResult && hooks.onToolResult(frame.toolCallId, parsedOutput)
            } else if (t === 'error') {
                hooks.onError && hooks.onError(frame.errorText || frame.message || 'agent error')
            }
        }
    }
}
